import axios from "axios";
import { getVisitorId } from "./visitorId";
import { currentUtmParams } from "./visitUtm";
import { isSalesRoute } from "./salesRoute";
import { INTEGRATION_SERVER_URL, CHECKOUT_PREFIX } from "../config/config";

/**
 * Thin client over the existing regional-plans endpoints, treating each
 * offer as a (region = "offers", version = offerSlug) pair.
 *
 * - GET  /apps/regionalplans/t1/config/offers/:offerSlug
 * - POST /apps/regionalplans/t1/create-intent-for-plans
 *
 * No new controller/route on the server — just two new config entries
 * (regions + plans + pricing) under the "offers" region.
 */

const REGION = "offers";

const client = axios.create({
  baseURL: INTEGRATION_SERVER_URL + "/regionalplans/t1",
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

// The visitor id rides on requests the page already makes, so page-visit
// analytics costs no extra round trip. See services/visitorId.
client.interceptors.request.use((cfg) => {
  try {
    cfg.headers = { ...(cfg.headers || {}), "x-visitor-id": getVisitorId() };
    // The ad's utm params are in the page url, not on this request — forward
    // them so a recorded visit can be attributed to the campaign.
    if (String(cfg.url || "").startsWith("/config/")) {
      cfg.params = { ...(cfg.params || {}), ...currentUtmParams() };
    }
    // Sales-mirror routes (INT-56): tag every request so the server records
    // the page visit as `isSales` (recordPageVisit reads ?channel) and keeps
    // it off the founder dashboard. A query param, so no CORS preflight for a
    // new header. create-intent also carries `channel` in its body below.
    if (isSalesRoute()) {
      cfg.params = { ...(cfg.params || {}), channel: "sales" };
    }
  } catch {
    /* analytics must never block a request */
  }
  return cfg;
});

/** Separate axios instance for the shipping flow (different mount path). */
const shippingClient = axios.create({
  baseURL: INTEGRATION_SERVER_URL + "/offers/shipping/t1",
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

const normalizeError = (error) => {
  if (error.response) {
    return Promise.reject({
      message: error.response.data?.message || "Server error",
      errors: error.response.data?.errors,
      status: error.response.status,
      data: error.response.data,
    });
  }
  if (error.request) {
    return Promise.reject({
      message: "Network error — unable to connect to server",
      status: 0,
    });
  }
  return Promise.reject({
    message: error.message || "Unknown error occurred",
    status: -1,
  });
};

client.interceptors.response.use((r) => r.data, normalizeError);
shippingClient.interceptors.response.use((r) => r.data, normalizeError);

/**
 * Fallback form for offers that don't declare `formFields` in config — i.e.
 * the original automation-lite pages. `column` routes each answer to a
 * standard customer column, the same mechanism the webinar checkout uses.
 */
export const DEFAULT_OFFER_FORM_FIELDS = [
  {
    key: "name",
    type: "text",
    label: "Full Name",
    placeholder: "Enter your full name",
    required: true,
    column: "fullName",
    validation: { minLength: 2, maxLength: 80 },
  },
  {
    key: "phone",
    type: "phone",
    label: "WhatsApp Number",
    placeholder: "Enter your 10-digit mobile number",
    required: true,
    column: "phone",
  },
  {
    key: "storeName",
    type: "text",
    label: "Store Name",
    placeholder: "Enter your store name",
    required: true,
    column: "organizationName",
    validation: { minLength: 2, maxLength: 100 },
  },
];

/**
 * Post-payment destination for a digital-only offer. Mirrors the two-step
 * plans checkout (`/thankyou-{region}?slug=...`) so a single-page checkout
 * lands the buyer on the same confirmation, with no customer _id needed.
 */
export const thankYouPath = (offerSlug, utmParams = {}) =>
  `/thankyou-${REGION}?${new URLSearchParams({
    slug: `${REGION}-${offerSlug}`,
    ...utmParams,
  }).toString()}`;

/**
 * Split answers into standard customer columns (via each field's `column`
 * hint) and generic custom responses, so backend persistence stays generic.
 * Empty answers are skipped so an untouched optional field never blanks a
 * column.
 */
export const splitFormResponses = (fields, values) => {
  const standard = {};
  const customResponses = {};
  (fields || []).forEach((field) => {
    const value = values[field.key];
    if (value == null || value === "") return;
    if (field.column) standard[field.column] = value;
    else customResponses[field.key] = value;
  });
  return { standard, customResponses };
};

/**
 * Maps the regional-plans config response into the flat "offer" shape
 * the OfferCheckout UI expects. Each offer is encoded as:
 *   - regionConfig (currency, titles, etc.)
 *   - exactly one plan in `plans[]`
 *   - exactly one pricing entry in `planOptions.monthly`
 */
const PERIOD_LABEL = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  yearly: "Yearly",
};

/**
 * Everything about an offer that changes when the buyer switches billing
 * cycle. Kept in one place so the switch can never update the price while
 * leaving, say, the value pills or the plan sent to the gateway behind.
 */
const billingOptionFor = (raw, plan) => {
  const period = plan.fixedBillingPeriod || "monthly";
  const pricing = (raw.planOptions?.[period] || {})[plan.id] || {};
  const price = Number(pricing.price || 0);
  const mrp = Number(pricing.originalPrice || 0);
  return {
    period,
    // Grouping key for combo pages that present several distinct products on
    // one page (e.g. WhatsApp vs AI Agent). Undefined for every existing
    // single-product page, so the panel's grouping stays inert for them.
    group: plan.group,
    // Per-plan "most popular" flag — drives which product card the combo page
    // pre-selects. Undefined/false for existing pages (harmless).
    popular: Boolean(plan.popular),
    label: PERIOD_LABEL[period] || period,
    // Single-card toggle label + optional second line; unset on existing pages.
    switchLabel: plan.switchLabel,
    switchSubLabel: plan.switchSubLabel,
    productName: plan.title || raw.title,
    tagline: plan.subtitle || raw.subtitle || "",
    price,
    mrp: mrp || price,
    savings: mrp > price ? mrp - price : 0,
    basePrice: pricing.basePrice != null ? Number(pricing.basePrice) : undefined,
    gstAmount: pricing.gstAmount != null ? Number(pricing.gstAmount) : undefined,
    billing: pricing.billed != null ? pricing.billed : "per month",
    discountLabel: raw.popularBadgeText || plan.highlight || "",
    includes: Array.isArray(plan.features)
      ? plan.features.map((f) => (typeof f === "string" ? f : f.text))
      : [],
    valuePills: Array.isArray(plan.valuePills) ? plan.valuePills : [],
    _plan: { id: plan.id, title: plan.title, price, billingPeriod: period },
  };
};

const mapToOffer = (slug, raw) => {
  const plans = (raw.plans || []).length ? raw.plans : [{}];
  const plan = plans[0];
  const billingPeriod = plan.fixedBillingPeriod || "monthly";
  const periodPricing = raw.planOptions?.[billingPeriod] || {};
  const pricing = periodPricing[plan.id] || {};

  const price = Number(pricing.price || 0);
  const mrp = Number(pricing.originalPrice || 0);
  // Optional: breakdown of the total into base + GST so the UI can render
  // them inline. Only present on offers where the server supplies them
  // (e.g. automation-lite-monthly). Falls back to undefined → UI shows the
  // flat total as before.
  const basePrice =
    pricing.basePrice != null ? Number(pricing.basePrice) : undefined;
  const gstAmount =
    pricing.gstAmount != null ? Number(pricing.gstAmount) : undefined;

  const gstPercentage = Number(raw.gstPercentage || 0);
  const kitAddonRaw = (raw.addons || []).find((a) => a.id === "qr-kit");
  const qrKitAddon = kitAddonRaw
    ? {
        id: kitAddonRaw.id,
        title: kitAddonRaw.title,
        basePrice: Number(kitAddonRaw.price),
      }
    : null;

  const includes = Array.isArray(plan.features)
    ? plan.features.map((f) => (typeof f === "string" ? f : f.text))
    : [];

  return {
    slug,
    productName: plan.title || raw.title,
    tagline: plan.subtitle || raw.subtitle || "",
    price,
    basePrice,
    gstAmount,
    gstPercentage,
    qrKitAddon,
    mrp: mrp || price,
    currency: raw.currencySymbol,
    currencyCode: raw.currency,
    // An explicit "" in config means "hide the billing-cycle line". Only a
    // missing value falls back to the default.
    billing: pricing.billed != null ? pricing.billed : "per month",
    discountLabel: raw.popularBadgeText || plan.highlight || "",
    savings: mrp > price ? mrp - price : 0,
    freeBonusLabel: "",
    ctaText: raw.checkoutButtonText || "",
    includes,
    /**
     * Value-stack pills under the headline. Display only — they sum to `mrp`
     * (the struck "total value"), while `price` is what is charged.
     */
    valuePills: Array.isArray(plan.valuePills) ? plan.valuePills : [],
    /**
     * Digital-only pages ship nothing, so there is no shipping step: payment
     * success goes straight to the plans thank-you page, exactly like the
     * two-step plans checkout does.
     */
    digitalOnly: Boolean(raw.digitalOnly),
    /** Opt-out of the "NEED HELP?" support block. */
    hideSupportContact: Boolean(raw.hideSupportContact),
    /** Headline price shows the GST-inclusive total rather than base + GST. */
    showGstInclusivePrice: Boolean(raw.showGstInclusivePrice),
    /**
     * Keep the "base + GST" headline but spell out the charged total on a
     * sub-line beneath it. Mirror of showGstInclusivePrice — the headline
     * ternaries treat that flag as the winner if both are somehow set.
     */
    showGstInclusiveSubNote: Boolean(raw.showGstInclusiveSubNote),
    /** Plain white page + 18px card corners. */
    flatPageStyle: Boolean(raw.flatPageStyle),
    /** Per-page analytics stack; undefined => legacy hardcoded stack. */
    tracking: raw.tracking,
    /** Seeds the country selector on international phone fields. */
    defaultCountry: raw.defaultCountry,
    /** Show "Request a Callback" beside Pay Now. */
    showCallbackRequest: Boolean(raw.showCallbackRequest),
    collapseBenefits: Boolean(raw.collapseBenefits),
    /**
     * Form fields come from the region config when the offer declares them
     * (config-driven, the same mechanism the webinar checkout uses). Offers
     * that declare none keep the original automation-lite form.
     */
    formFields:
      Array.isArray(raw.formFields) && raw.formFields.length > 0
        ? raw.formFields
        : DEFAULT_OFFER_FORM_FIELDS,
    /** Server-verified plan reference used by create-intent-for-plans. */
    _plan: {
      id: plan.id,
      title: plan.title,
      price,
      billingPeriod: plan.fixedBillingPeriod || "monthly",
    },
    /**
     * One entry per plan the page offers, so a single page can sell the same
     * product on more than one billing cycle (e.g. monthly + yearly behind a
     * switch). Every field here is one the switch must change; the panel
     * spreads the selected entry over the offer, so the rest of the UI keeps
     * reading the same names.
     *
     * Pages with a single plan get a one-entry array and render exactly as
     * before — the switch only appears when there is a real choice.
     */
    billingOptions: plans.map((p) => billingOptionFor(raw, p)),
  };
};

class OfferApiService {
  async getOfferConfig(offerSlug) {
    const envelope = await client.get(`/config/${REGION}/${offerSlug}`);
    return mapToOffer(offerSlug, envelope.data);
  }

  /**
   * Cart-recovery pre-fill: exchange the encrypted `?prefill=` token from a
   * recovery link for the buyer's saved fields (server-side decrypt). Returns
   * an object of fields, or null. Best-effort — never throws, so a bad/absent
   * token just leaves the form blank.
   */
  async getPrefill(token) {
    try {
      // The client's response interceptor already unwraps to the body
      // (`r => r.data`), so `res` is `{ success, data }` — the fields are at
      // `res.data` (one level), exactly like getOfferConfig's `envelope.data`.
      const res = await client.get(`/prefill`, { params: { token } });
      return (res && res.data) || null;
    } catch {
      return null;
    }
  }

  /**
   * @param offerSlug
   * @param offer  the flat offer shape returned by getOfferConfig
   * @param form   { name, email, phone, ... }
   * @param countryContext  { dialCode, isoCode }
   * @param utmParams
   */
  async createPaymentIntent(
    offerSlug,
    offer,
    form,
    countryContext = { dialCode: "+91", isoCode: "IN" },
    utmParams = {},
    kitSelected = false,
    requestCallback = false,
  ) {
    // Email is sent only when the offer's form collects one — automation-lite
    // collects storeName instead. Required-ness is enforced server-side from
    // the same field config that rendered the form.
    const { standard, customResponses } = splitFormResponses(
      offer.formFields,
      form
    );

    const payload = {
      // Ties this conversion back to the page view that produced it. Offer
      // checkouts always create a FRESH customer doc, so without this they
      // could never be linked to a visit.
      visitorId: getVisitorId(),
      fullName: standard.fullName,
      ...(standard.email ? { email: standard.email } : {}),
      phone: standard.phone,
      ...(standard.organizationName
        ? { organizationName: standard.organizationName }
        : {}),
      ...(standard.organizationSize
        ? { organizationSize: standard.organizationSize }
        : {}),
      ...(Object.keys(customResponses).length ? { customResponses } : {}),
      region: REGION,
      version: offerSlug,
      // Sales-mirror lead (INT-56). The server persists this on the customer
      // doc, so both the lead notify AND the later payment notify keep only
      // LeadSquared (no WhatsApp/Slack/Pabbly), fire no cart-recovery webhook,
      // and stamp the record `isSales` so it never reaches the dashboard.
      ...(isSalesRoute() ? { channel: "sales" } : {}),
      currency: offer.currencyCode,
      plan: offer._plan,
      addons:
        kitSelected && offer.qrKitAddon ? [{ id: offer.qrKitAddon.id }] : [],
      totalAmount: offer.price,
      currencySymbol: offer.currency,
      source: "aisensy_offers",
      countryCallingCode: countryContext.dialCode,
      isoCode: countryContext.isoCode,
      // Cancel returns to whichever path the buyer is actually on, so this
      // keeps working across the /offer and /checkout route aliases.
      cancelUrl: `${CHECKOUT_PREFIX}${
        typeof window !== "undefined"
          ? window.location.pathname
          : `/offer/${offerSlug}`
      }`,
      // Digital-only pages have no shipping step — they land on the plans
      // thank-you page like the two-step plans checkout. Physical offers
      // (automation-lite + QR kit) keep the shipping step.
      redirectUrl: offer.digitalOnly
        ? `${CHECKOUT_PREFIX}${thankYouPath(offerSlug, utmParams)}`
        : `${CHECKOUT_PREFIX}/offer/${offerSlug}/shipping`,
      utmParams,
      offerMeta: { storeName: form.storeName || "" },
      // Deliberately the SAME endpoint and payload as Pay Now, so the lead is
      // captured identically; the server just stops before the gateway.
      ...(requestCallback ? { requestCallback: true } : {}),
    };

    const envelope = await client.post("/create-intent-for-plans", payload);
    return envelope.data; // { paymentGateway, intentId, keyId, currency, customerId, ... }
  }

  /** Shipping page — fetch context (paid flag + prefill). */
  async getShippingContext(customerId) {
    const envelope = await shippingClient.get(`/context/${customerId}`);
    return envelope.data; // { customerId, offerSlug, paid, shippingStatus, prefill, shopifyOrderName? }
  }

  /** Shipping page — submit email + address. */
  async submitShipping(customerId, { email, address }) {
    const envelope = await shippingClient.post(`/submit/${customerId}`, {
      email,
      address,
    });
    return envelope.data; // { customerId, shopifyOrderName? }
  }

  /**
   * Non-blocking pincode → {city, state} lookup via India Post's public
   * API. Returns null on any error / timeout (1.5s). Never throws.
   * The shipping form consumes this only to pre-fill fields the user
   * can still edit; it is never a validation gate.
   */
  async lookupPincode(pincode) {
    if (!/^\d{6}$/.test(String(pincode || ""))) return null;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 1500);
    try {
      const res = await fetch(
        `https://api.postalpincode.in/pincode/${pincode}`,
        { signal: controller.signal },
      );
      if (!res.ok) return null;
      const data = await res.json();
      const entry = Array.isArray(data) && data[0];
      if (!entry || entry.Status !== "Success") return null;
      const po = entry.PostOffice && entry.PostOffice[0];
      if (!po) return null;
      return {
        city: po.District || po.Block || po.Name || "",
        state: po.State || "",
      };
    } catch {
      return null;
    } finally {
      clearTimeout(timeout);
    }
  }
}

export const offerApiService = new OfferApiService();
export default offerApiService;
